import { useEffect, useState } from "react";
import LearnMore from "@/components/LearnMore";
import Navbar from "@/components/Navbar";
import ResetPlayer from "@/components/ResetPlayer";
import { Pin, Loader2, Send } from "lucide-react";
import { shareReset } from "@/lib/share";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { api } from "@/lib/apiClient";
import { useResetLibrary } from "@/lib/resets";
import { primeBreath } from "@/lib/breathSound";

export default function Resets() {
  const [active, setActive] = useState(null);
  const lib = useResetLibrary();
  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get("play");
    const r = id && (lib || []).find((x) => x.id === id);
    if (r) setActive(r);
  }, [lib]);
  const { user, setUser } = useAuth();
  const fav = user?.profile?.favourite_reset;
  const pin = async (r) => {
    const id = fav === r.id ? null : r.id;
    await api.post("/resets/favourite", { id });
    setUser((u) => ({ ...u, profile: { ...u.profile, favourite_reset: id } }));
    toast(id ? `${r.title} is your go-to reset — it's on your Routine page.` : "Go-to reset removed.");
  };
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-3xl lg:max-w-5xl mx-auto px-5 sm:px-8 py-10">
        <p className="text-xs uppercase tracking-[0.25em] text-primary mb-2">Quick Resets</p>
        <h1 className="font-serif text-4xl sm:text-5xl mb-3">Escape the spiral in 90 seconds.</h1>
        <div className="mb-4"><LearnMore area="resets" /></div>
        <p className="text-muted-foreground max-w-xl mb-10 leading-relaxed">A short, guided state-shift for whatever just happened. Breathe with it — you'll come back to centre.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="resets-grid">
          {!lib && <Loader2 className="w-5 h-5 animate-spin text-primary" />}
          {(lib || []).map((r) => (
            <div key={r.id} className="relative h-full">
              <button onClick={() => { primeBreath(); setActive(r); }} data-testid={`reset-card-${r.id}`}
                className={`group w-full h-full flex items-center gap-4 rounded-2xl border p-5 ${fav === r.id ? "pr-24" : "pr-14"} text-left hover:border-primary/40 hover:-translate-y-0.5 transition-all duration-300 ${fav === r.id ? "border-primary/50 bg-primary/[0.04]" : "border-border"}`}>
                <span className="w-12 h-12 rounded-full flex items-center justify-center shrink-0" style={{ background: `${r.accent}22`, color: r.accent }}><r.icon className="w-5 h-5" /></span>
                <div><div className="font-serif text-xl">{r.title}</div><div className="text-sm text-muted-foreground">{fav === r.id ? "Your go-to reset" : r.sub}</div></div>
              </button>
              {fav === r.id && (
                <button onClick={() => shareReset(r)} aria-label="Send to a friend" data-testid={`reset-share-${r.id}`}
                  className="absolute top-1/2 -translate-y-1/2 right-14 w-10 h-10 rounded-full flex items-center justify-center text-primary hover:bg-primary/10 transition-colors duration-200">
                  <Send className="w-4 h-4" />
                </button>
              )}
              <button onClick={() => pin(r)} aria-label={fav === r.id ? "Unpin go-to reset" : "Pin as go-to reset"} data-testid={`reset-pin-${r.id}`} data-pinned={fav === r.id}
                className={`absolute top-1/2 -translate-y-1/2 right-3 w-10 h-10 rounded-full flex items-center justify-center transition-colors duration-200 ${fav === r.id ? "text-primary bg-primary/10" : "text-muted-foreground/60 hover:text-foreground hover:bg-white/5"}`}>
                <Pin className="w-4 h-4" fill={fav === r.id ? "currentColor" : "none"} />
              </button>
            </div>
          ))}
        </div>
      </main>
      {active && <ResetPlayer reset={active} onClose={() => setActive(null)} />}
    </div>
  );
}
